import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import {
  addDecimals,
  compareDecimalStrings,
  divideDecimals,
  normalizeDecimal,
  percentageOfDecimal,
  roundUpDecimal,
  subtractDecimals,
} from "@/shared/utils/decimal";
import type { PaymentTaxBearer } from "@/shared/utils/paymentTaxes";

import type { PaymentDirection, PaymentFeeRail } from "../types/paymentFees.types";
import type { PaymentTax, TaxScopeGroup, TaxSummary } from "../types/paymentTaxes.types";

/** Mirrors coloca-nest `UNTAXED_CURRENCIES`. */
const UNTAXED_CURRENCIES = new Set(["USDT"]);

export interface TaxScope {
  country: string;
  rail: PaymentFeeRail;
  direction: PaymentDirection;
  currency: string;
  organizationId: string | null;
}

export interface AppliedTax {
  tax: PaymentTax;
  baseAmount: string;
  amount: string;
}

export interface AppliedTaxes {
  lines: AppliedTax[];
  /** What the client pays on top of the commission. */
  clientTotal: string;
  /** What Coloca absorbs. */
  colocaTotal: string;
}

const validFromMs = (tax: PaymentTax) => new Date(tax.valid_from).getTime();

/** Same weights as the backend: organization 4, rail 2, direction 1 — never a plain column count. */
export function taxSpecificity(tax: Pick<PaymentTax, "organization_id" | "rail" | "direction">): number {
  return (tax.organization_id ? 4 : 0) + (tax.rail ? 2 : 0) + (tax.direction ? 1 : 0);
}

function matches(tax: PaymentTax, scope: Omit<TaxScope, "currency">): boolean {
  return (
    tax.country === scope.country &&
    (!tax.rail || tax.rail === scope.rail) &&
    (!tax.direction || tax.direction === scope.direction) &&
    (!tax.organization_id || tax.organization_id === scope.organizationId)
  );
}

/** Port of `resolvePaymentTaxes`: one row per code, most specific first, latest version on a tie, rate 0 out. */
export function resolveTaxes(rows: PaymentTax[], scope: TaxScope, now: number): PaymentTax[] {
  return resolveTaxWinners(rows, scope, now).filter((tax) => compareDecimalStrings(tax.rate, "0") > 0);
}

/** The winning row of every code, an exemption (rate 0) included. */
export function resolveTaxWinners(rows: PaymentTax[], scope: TaxScope, now: number): PaymentTax[] {
  if (UNTAXED_CURRENCIES.has(scope.currency)) return [];
  const live = rows
    .filter((tax) => validFromMs(tax) <= now && matches(tax, scope))
    .sort((a, b) => validFromMs(b) - validFromMs(a));
  const winners = new Map<string, PaymentTax>();
  for (const tax of live) {
    const best = winners.get(tax.code);
    if (!best || taxSpecificity(tax) > taxSpecificity(best)) winners.set(tax.code, tax);
  }
  return [...winners.values()].sort((a, b) =>
    a.base === b.base ? a.code.localeCompare(b.code) : a.base === "COMMISSION" ? -1 : 1,
  );
}

/** Rounded up to the currency's minor units, as the backend charges it. */
export function taxAmount(baseAmount: string, rate: string, currency: string): string {
  return roundUpDecimal(percentageOfDecimal(baseAmount, rate), minorUnitsFor(currency));
}

export interface TaxAmounts {
  grossAmount: string;
  commission: string;
  currency: string;
  /** A payout's client-borne fees: its taxes on the amount then go on what the beneficiary receives. */
  payoutFees?: string;
}

/** Port of coloca-nest `applyPaymentTaxes`. A tax at 0 is left out. */
export function applyTaxes(taxes: PaymentTax[], amounts: TaxAmounts): AppliedTaxes {
  const commission = normalizeDecimal(amounts.commission);
  const onCommission = new Map(
    taxes
      .filter((tax) => tax.base === "COMMISSION")
      .map((tax) => [tax.id, taxAmount(commission, tax.rate, amounts.currency)]),
  );
  const onAmount = amountTaxes(taxes, amounts, onCommission);
  const lines = taxes
    .map((tax) => {
      const commissionAmount = onCommission.get(tax.id);
      return {
        tax,
        baseAmount: commissionAmount === undefined ? onAmount.base : commission,
        amount: commissionAmount ?? onAmount.totals.get(tax.id) ?? "0",
      };
    })
    .filter((line) => compareDecimalStrings(line.amount, "0") > 0);
  const totalOf = (bearer: PaymentTaxBearer) =>
    lines
      .filter((line) => line.tax.bearer === bearer)
      .reduce((sum, line) => addDecimals(sum, line.amount), "0");
  return { lines, clientTotal: totalOf("CLIENT"), colocaTotal: totalOf("COLOCA") };
}

/** A payin's amount is what the payer sends; a payout's is what the beneficiary receives. */
function amountTaxes(
  taxes: PaymentTax[],
  amounts: TaxAmounts,
  onCommission: Map<string, string>,
): { base: string; totals: Map<string, string> } {
  const gross = normalizeDecimal(amounts.grossAmount);
  const onAmount = taxes.filter((tax) => tax.base === "GROSS_AMOUNT");
  if (amounts.payoutFees === undefined) {
    return {
      base: gross,
      totals: new Map(onAmount.map((tax) => [tax.id, taxAmount(gross, tax.rate, amounts.currency)])),
    };
  }
  const decimals = minorUnitsFor(amounts.currency);
  const clientCommissionTaxes = taxes
    .filter((tax) => tax.bearer === "CLIENT")
    .reduce((sum, tax) => addDecimals(sum, onCommission.get(tax.id) ?? "0"), "0");
  const leftover = subtractDecimals(subtractDecimals(gross, amounts.payoutFees), clientCommissionTaxes);
  const available = compareDecimalStrings(leftover, "0") > 0 ? leftover : "0";
  const clientOnAmount = onAmount.filter((tax) => tax.bearer === "CLIENT");
  const share = addDecimals(
    "1",
    divideDecimals(
      clientOnAmount.reduce((sum, tax) => addDecimals(sum, tax.rate), "0"),
      "100",
    ),
  );
  const totals = new Map(
    clientOnAmount.map((tax) => [
      tax.id,
      roundUpDecimal(divideDecimals(percentageOfDecimal(available, tax.rate), share, "up"), decimals),
    ]),
  );
  const delivered = subtractDecimals(
    available,
    [...totals.values()].reduce((sum, value) => addDecimals(sum, value), "0"),
  );
  for (const tax of onAmount) {
    if (!totals.has(tax.id)) totals.set(tax.id, taxAmount(delivered, tax.rate, amounts.currency));
  }
  return { base: delivered, totals };
}

export function taxScopeKey(
  tax: Pick<PaymentTax, "country" | "code" | "organization_id" | "rail" | "direction">,
) {
  return [tax.country, tax.code, tax.organization_id ?? "", tax.rail ?? "", tax.direction ?? ""].join("|");
}

function scopeGroups(rows: PaymentTax[], now: number): TaxScopeGroup[] {
  const byScope = new Map<string, PaymentTax[]>();
  for (const tax of rows) {
    const key = taxScopeKey(tax);
    byScope.set(key, [...(byScope.get(key) ?? []), tax]);
  }
  return [...byScope.entries()]
    .map(([key, versions]) => {
      const sorted = [...versions].sort((a, b) => validFromMs(b) - validFromMs(a));
      const head = sorted[0];
      return {
        key,
        code: head.code,
        name: (sorted.find((tax) => validFromMs(tax) <= now) ?? head).name,
        country: head.country,
        base: head.base,
        organizationId: head.organization_id,
        rail: head.rail,
        direction: head.direction,
        current: sorted.find((tax) => validFromMs(tax) <= now) ?? null,
        versions: sorted,
      };
    })
    .sort((a, b) => taxSpecificity(versionOf(a)) - taxSpecificity(versionOf(b)));
}

const versionOf = (group: TaxScopeGroup) => ({
  organization_id: group.organizationId,
  rail: group.rail,
  direction: group.direction,
});

/**
 * The organization's own scope the backend applies on a general row: any in force that covers it, most
 * specific first.
 */
export function effectiveOwnScope(general: TaxScopeGroup, own: TaxScopeGroup[]): TaxScopeGroup | null {
  return own
    .filter(
      (group) =>
        group.current !== null &&
        (!group.rail || group.rail === general.rail) &&
        (!group.direction || group.direction === general.direction),
    )
    .reduce<TaxScopeGroup | null>(
      (best, group) =>
        !best || taxSpecificity(versionOf(group)) > taxSpecificity(versionOf(best)) ? group : best,
      null,
    );
}

/** Organizations whose own scope replaces this general row. */
export function exceptionsOf(summary: TaxSummary, general: TaxScopeGroup): number {
  const organizations = new Set(summary.organizationScopes.map((group) => group.organizationId));
  return [...organizations].filter((organizationId) =>
    effectiveOwnScope(
      general,
      summary.organizationScopes.filter((group) => group.organizationId === organizationId),
    ),
  ).length;
}

/** The general rows an organization's own scope is the one applied on. */
export function rowsCoveredBy(summary: TaxSummary, own: TaxScopeGroup): TaxScopeGroup[] {
  const ownScopes = summary.organizationScopes.filter((group) => group.organizationId === own.organizationId);
  return summary.scopes.filter((general) => effectiveOwnScope(general, ownScopes)?.key === own.key);
}

export type TaxHistoryAccess = "editable" | "general" | "inherited";

/** A version is corrected only from the row that owns it in the current view. */
export function taxHistoryAccess(
  group: TaxScopeGroup,
  scopeOrgId: string | null,
  inherited: boolean,
): TaxHistoryAccess {
  if (group.organizationId !== scopeOrgId) return "general";
  return inherited ? "inherited" : "editable";
}

/** Countries → taxes → scopes, the way the page lists them. */
export function summarizeTaxes(rows: PaymentTax[], now: number): Map<string, TaxSummary[]> {
  const byTax = new Map<string, PaymentTax[]>();
  for (const tax of rows) {
    const key = `${tax.country}|${tax.code}`;
    byTax.set(key, [...(byTax.get(key) ?? []), tax]);
  }
  const byCountry = new Map<string, TaxSummary[]>();
  for (const [key, taxRows] of byTax.entries()) {
    const groups = scopeGroups(taxRows, now);
    const general = groups.filter((group) => !group.organizationId);
    const head = general[0] ?? groups[0];
    const summary: TaxSummary = {
      key,
      code: head.code,
      name: head.name,
      country: head.country,
      base: head.base,
      scopes: general,
      organizationScopes: groups.filter((group) => group.organizationId),
    };
    byCountry.set(summary.country, [...(byCountry.get(summary.country) ?? []), summary]);
  }
  for (const list of byCountry.values()) {
    list.sort((a, b) =>
      a.base === b.base ? a.code.localeCompare(b.code) : a.base === "COMMISSION" ? -1 : 1,
    );
  }
  return byCountry;
}
